import { readPreference, writePreference } from '@/composables/usePreference';
import { computed, onMounted, ref, watch } from 'vue';

export type LandingTheme = 'dark' | 'light';

const getSystemTheme = (): LandingTheme => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
        return 'light';
    }

    try {
        return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    } catch {
        return 'light';
    }
};

export function useLandingTheme() {
    // IMPORTANT: keep the initial value deterministic for hydration.
    // The landing page prerender is generated with `light`, so initializing from
    // localStorage/matchMedia here would create hydration mismatches.
    const theme = ref<LandingTheme>('light');
    const isLight = computed(() => theme.value === 'light');

    const toggleTheme = () => {
        theme.value = theme.value === 'light' ? 'dark' : 'light';
    };

    onMounted(() => {
        const preferred = readPreference<LandingTheme>('landingTheme', getSystemTheme());
        theme.value = preferred;

        watch(
            theme,
            (value) => {
                writePreference<LandingTheme>('landingTheme', value);
                document.documentElement.dataset.landingTheme = value;
            },
            { immediate: true },
        );
    });

    return {
        theme,
        isLight,
        toggleTheme,
    };
}
